import { countComments, discussionKeepMs, ROOTS_PER_PAGE, type Comment, type DiscussionPage, type DiscussionState } from "./discussion";
import { parseDiscussionResult, unavailable, windowThreads, type DiscussionFailure, type DiscussionRecord, type DiscussionResult, type DiscussionWindow } from "./discussionWindow";

export interface DiscussionReader { load(cursor?: string, fresh?: boolean): Promise<DiscussionResult | DiscussionPage> }
export interface DiscussionSnapshot {
  phase: "idle" | "loading" | "ready"; window: DiscussionWindow | null; threads: Comment[];
  more: boolean; busy: boolean; stale: boolean; failure?: DiscussionFailure;
  moreFailed: boolean; replyBusy: ReadonlySet<string>; replyFailed: ReadonlySet<string>;
}

export type DiscussionViewState = DiscussionState | "waiting" | "restricted";
/** Content evidence and the latest attempt are independent, including for an empty saved copy. */
export function discussionState(s: Pick<DiscussionSnapshot, "window" | "failure">): DiscussionViewState {
  if (s.failure === "disabled") return "none";
  if (s.window && (s.window.state !== "none" || !s.failure)) return s.window.state;
  return s.failure === "waiting" || s.failure === "restricted" || s.failure === "blocked" ? s.failure : "failed";
}

/** The only legacy boundary: old endpoints and reader fixtures can finish their existing visits. */
export function pageResult(page: DiscussionPage): DiscussionResult {
  if (page.state === "failed" || page.state === "blocked") return unavailable(page.state, page.link, page.fetchedAt);
  const records: DiscussionRecord[] = [];
  for (const root of page.threads) {
    const { replies, ...head } = root;
    records.push({ ...head, ...(replies ? { replyCount: replies.length } : {}) });
    for (const reply of replies ?? []) records.push({ ...reply, parent: root.id });
  }
  return { kind: "window", window: { version: 2, state: page.state, records, link: page.link, fetchedAt: page.fetchedAt, ...(page.total !== undefined ? { total: page.total } : {}), ...(page.next ? { next: page.next } : {}) } };
}

/** Data/request state shared by both hosts. DOM expansion, focus and scroll belong to the view. */
export class DiscussionSession {
  private phase: DiscussionSnapshot["phase"] = "idle";
  private first: DiscussionWindow | null = null;
  private records = new Map<string, DiscussionRecord>();
  private visible = ROOTS_PER_PAGE;
  private next: string | undefined;
  private busy = false;
  private failure: DiscussionFailure | undefined;
  private moreFailed = false;
  private replyBusy = new Set<string>();
  private replyFailed = new Set<string>();
  private generation = 0;
  private disposed = false;
  private windows = 0;
  private attemptedAt = 0;
  constructor(private readonly reader: DiscussionReader, private readonly changed: () => void, private readonly link: string) {}
  snapshot(): DiscussionSnapshot {
    const all = windowThreads(this.records.values());
    return { phase: this.phase, window: this.first, threads: all.slice(0, this.visible), more: all.length > this.visible || !!this.next, busy: this.busy, stale: !!this.first && !!this.failure && this.failure !== "disabled", failure: this.failure, moreFailed: this.moreFailed, replyBusy: this.replyBusy, replyFailed: this.replyFailed };
  }
  record(id: string) { return this.records.get(id); }
  start() { if (this.phase === "idle" || (this.phase === "ready" && !this.busy && Date.now() - this.attemptedAt >= discussionKeepMs(this.failure ?? this.first?.state ?? "failed"))) void this.refresh(false); }
  pause() { this.generation++; this.busy = false; this.replyBusy.clear(); if (this.phase === "loading") this.phase = "idle"; this.emit(); }
  disable() { this.pause(); this.first = null; this.records.clear(); this.next = undefined; this.failure = "disabled"; this.phase = "idle"; this.emit(); }
  dispose() { this.disposed = true; this.generation++; }
  private emit() { if (!this.disposed) this.changed(); }
  private async read(cursor?: string, fresh = false): Promise<DiscussionResult> {
    try {
      const raw = await this.reader.load(cursor, fresh);
      const result = "kind" in raw ? parseDiscussionResult(raw) : parseDiscussionResult(pageResult(raw));
      return result ?? unavailable("failed", this.link);
    } catch { return unavailable("failed", this.link); }
  }
  private merge(window: DiscussionWindow) { this.windows++; for (const r of window.records) this.records.set(r.id, { ...r }); }
  private cap() {
    if (this.windows < 20) {
      if (this.first?.partial && this.first.total !== undefined && !this.next && ![...this.records.values()].some(record => record.replyNext) && countComments(windowThreads(this.records.values())) >= this.first.total) this.first = { ...this.first, partial: false };
      return;
    }
    this.next = undefined;
    for (const record of this.records.values()) delete record.replyNext;
    if (this.first) this.first = { ...this.first, partial: true };
  }
  async refresh(fresh = true) {
    if (this.disposed) return;
    const mine = ++this.generation;
    if (!this.first) this.phase = "loading";
    this.busy = true; this.emit();
    const result = await this.read(undefined, fresh);
    if (this.disposed || mine !== this.generation) return;
    this.attemptedAt = Date.now();
    this.busy = false; this.moreFailed = false; this.replyBusy.clear(); this.replyFailed.clear();
    if (result.kind === "window") {
      this.first = result.window; this.records.clear(); this.windows = 0; this.merge(result.window);
      this.next = result.window.next; this.visible = ROOTS_PER_PAGE; this.failure = undefined;
    } else if (result.reason === "disabled") {
      this.first = null; this.records.clear(); this.next = undefined; this.failure = "disabled";
    } else {
      if (!this.first && result.cached) { this.first = result.cached; this.merge(result.cached); this.next = result.cached.next; }
      this.failure = result.reason;
    }
    this.cap(); this.phase = "ready"; this.emit();
  }
  async more() {
    if (this.disposed || this.busy || this.replyBusy.size) return;
    if (windowThreads(this.records.values()).length > this.visible) { this.visible += ROOTS_PER_PAGE; this.emit(); return; }
    if (!this.next) return;
    const mine = this.generation;
    this.busy = true; this.moreFailed = false; this.emit();
    const result = await this.read(this.next);
    if (this.disposed || mine !== this.generation) return;
    this.busy = false;
    if (result.kind === "window") {
      this.merge(result.window); this.next = result.window.next; this.visible += ROOTS_PER_PAGE;
      if (this.first) { this.first = { ...this.first, total: result.window.total ?? this.first.total, partial: result.window.partial ?? this.first.partial }; }
      if (this.first && this.first.total === undefined && !this.next && !result.window.partial && ![...this.records.values()].some(r => r.replyNext)) this.first.total = countComments(windowThreads(this.records.values()));
    } else if (result.reason === "disabled") { this.disable(); return; }
    else { this.moreFailed = true; this.failure = result.reason; }
    this.cap(); this.emit();
  }
  async replies(id: string) {
    const record = this.records.get(id);
    if (!record?.replyNext || this.replyBusy.size || this.busy || this.disposed) return;
    const mine = this.generation;
    this.replyBusy.add(id); this.replyFailed.delete(id); this.emit();
    const result = await this.read(record.replyNext);
    if (this.disposed || mine !== this.generation) return;
    this.replyBusy.delete(id);
    if (result.kind === "window") { this.merge(result.window); record.replyNext = result.window.next; }
    else if (result.reason === "disabled") { this.disable(); return; }
    else { this.replyFailed.add(id); this.failure = result.reason; }
    this.cap(); this.emit();
  }
}
